import type { Session } from "./api";

export const SESSION_KEY = "pioneer-meet:session";
export const SESSION_REJECTED = "pioneer-meet:session-rejected";
const nameKey = (address: string) => `pioneer-meet:name:${address.toLowerCase()}`;

// Expiry decoding is only a local UX check. Alpha still verifies every signature
// and authenticated request; browser storage never grants authority.
function validSession(value: any): value is Session {
	try {
		if (!/^0x[0-9a-f]{40}$/i.test(value?.address) || typeof value?.token !== "string") return false;
		const parts = value.token.split(".");
		if (parts.length !== 3) return false;
		const claims = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
		if (typeof claims.address !== "string" || claims.address.toLowerCase() !== value.address.toLowerCase()) return false;
		if (!Number.isSafeInteger(claims.exp) || (value.expiresAt !== undefined && !Number.isSafeInteger(value.expiresAt))) return false;
		const expiry = Math.min(claims.exp, value.expiresAt ?? claims.exp);
		return Number.isFinite(expiry) && expiry * 1000 > Date.now();
	} catch { return false; }
}

export function readSession(): Session | null {
	try {
		const raw = localStorage.getItem(SESSION_KEY);
		if (!raw) return null;
		const session = JSON.parse(raw);
		if (validSession(session)) return session;
	} catch { /* corrupt or unavailable storage does not prevent a fresh login */ }
	clearSession();
	return null;
}

export function saveSession(session: Session) {
	clearSession();
	try {
		if (validSession(session)) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
	} catch { /* private/blocked storage: keep the live in-memory session */ }
}

export function clearSession() {
	try { localStorage.removeItem(SESSION_KEY); } catch {}
}

export function rejectSession(token: string) {
	// A late failure from an old request must not log out a newer signed session.
	try {
		const raw = localStorage.getItem(SESSION_KEY);
		if (raw && JSON.parse(raw).token !== token) return;
	} catch {}
	clearSession();
	window.dispatchEvent(new Event(SESSION_REJECTED));
}

export function readName(address?: string): string {
	try { return address ? (localStorage.getItem(nameKey(address)) || "").slice(0, 60) : ""; }
	catch { return ""; }
}

export function saveName(address: string, name: string) {
	try { localStorage.setItem(nameKey(address), name.slice(0, 60)); } catch {}
}
